import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmtErr, wsUrl } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Eye, MessageCircle, Heart, Timer, X } from "lucide-react";
import { logSwallow } from "../lib/log";
import { useTranslation } from "react-i18next";

function formatRemaining(seconds) {
  if (seconds == null || seconds <= 0) return "expired";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m >= 1) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const TABS = [
  { id: "blinks", labelKey: "inbox.tabBlinks", icon: Eye },
  { id: "chats", labelKey: "inbox.tabChats", icon: MessageCircle },
  { id: "comments", labelKey: "inbox.tabComments", icon: Heart },
];

export default function Inbox() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState({ blinks: [], chats: [], comments: [], unread: { total: 0 } });
  const [tab, setTab] = useState("blinks");
  const [now, setNow] = useState(Date.now());
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/inbox");
      setData(res.data);
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(load, 15000);
    // Realtime — react to any inbox-relevant WS event.
    // `ws` is captured in this effect's closure so cleanup closes exactly
    // the connection this effect opened (not a later one).
    let ws = null;
    try {
      const url = wsUrl("/user");
      if (url) {
        ws = new WebSocket(url);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (["interest.received", "match.new", "message.new", "vibe.comment", "comment.reaction"].includes(m.type)) {
              load();
            }
          } catch (e) { logSwallow("inbox.ws.parse", e); }
        };
        ws.onerror = (e) => logSwallow("inbox.ws.error", e);
        wsRef.current = ws;
      }
    } catch (e) { logSwallow("inbox.ws.open", e); }
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(t); clearInterval(poll);
      document.removeEventListener("visibilitychange", onVis);
      if (ws) { try { ws.close(); } catch (e) { logSwallow("inbox.ws.close", e); } }
      if (wsRef.current === ws) wsRef.current = null;
    };
  }, [load]);

  const blinkBack = async (it) => {
    try {
      const r = await api.post("/interests", { to_user: it.from_user.id, venue_id: it.venue_id });
      if (r.data.mutual) nav(`/match/${r.data.match.id}`);
      else load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const dismissBlink = async (it) => {
    try { await api.post(`/interests/${it.interest_id}/dismiss`); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const openBlink = async (it) => {
    if (!it.read) {
      try { await api.post(`/interests/${it.interest_id}/read`); }
      catch (e) { logSwallow("inbox.read.blink", e); }
    }
    load();
  };

  const openComment = async (c) => {
    if (!c.read) {
      try { await api.post(`/vibes/comments/${c.id}/read`); }
      catch (e) { logSwallow("inbox.read.comment", e); }
    }
    nav(`/vibe/${c.vibe_id}`);
  };

  const openChat = async (matchId) => {
    try { await api.post(`/chats/${matchId}/read`); }
    catch (e) { logSwallow("inbox.read.chat", e); }
    nav(`/chat/${matchId}`);
  };

  const counts = data.unread || {};

  return (
    <div className="min-h-screen bg-black text-white relative">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(230,0,0,0.12)_0%,transparent_70%)] pointer-events-none" />
      <div className="relative max-w-md mx-auto px-5 pt-6 pb-24">
        <div className="flex items-center justify-between" data-testid="inbox-header">
          <Link to="/home" className="flex items-center gap-2 text-zinc-300 hover:text-white" data-testid="inbox-back">
            <ArrowLeft size={18} className="rtl:rotate-180"/> <span>{t("nav.home")}</span>
          </Link>
          <span className="font-mono-accent text-zinc-400">{t("inbox.title")}</span>
          <span className="w-12" />
        </div>

        <h1 className="mt-5 text-5xl font-bold tracking-tight" style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 800 }}>
          {t("inbox.title")}
        </h1>
        <p className="mt-1 font-mono-accent text-zinc-500 tracking-[0.2em] text-[0.7rem]">
          {t("inbox.subtitle")}
        </p>

        {/* Tab bar */}
        <div className="mt-5 grid grid-cols-3 gap-2" data-testid="inbox-tabs">
          {TABS.map(tItem => {
            const IconEl = tItem.icon;
            const active = tab === tItem.id;
            const unread =
              tItem.id === "blinks" ? counts.blinks || 0 :
              tItem.id === "chats" ? counts.chats || 0 :
              counts.comments || 0;
            return (
              <button
                key={tItem.id}
                onClick={() => setTab(tItem.id)}
                data-testid={`inbox-tab-${tItem.id}`}
                className={`relative py-3 rounded-2xl border transition-all font-mono-accent text-[0.7rem] tracking-[0.2em] flex items-center justify-center gap-1.5 ${
                  active
                    ? "border-[#E60000] bg-gradient-to-b from-[#E60000]/20 to-[#660000]/10 shadow-[0_0_20px_rgba(230,0,0,0.3)] text-white"
                    : "border-white/10 bg-black/40 text-zinc-400 hover:border-white/30"
                }`}
              >
                <IconEl size={13} className={active ? "text-[#E60000]" : "text-zinc-500"} />
                {t(tItem.labelKey)}
                {unread > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#E60000] text-white text-[9px] font-bold">
                    {unread}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-6" data-testid={`inbox-section-${tab}`}>
          {tab === "blinks" && (
            <BlinksList items={data.blinks} now={now} onBlinkBack={blinkBack} onDismiss={dismissBlink} onOpen={openBlink} t={t} />
          )}
          {tab === "chats" && (
            <ChatsList items={data.chats} onOpen={openChat} t={t} />
          )}
          {tab === "comments" && (
            <CommentsList items={data.comments} onOpen={openComment} t={t} />
          )}
        </div>
      </div>
    </div>
  );
}

function BlinksList({ items, now, onBlinkBack, onDismiss, onOpen, t }) {
  if (!items.length) return <Empty label={t("inbox.noBlinks")} sub={t("inbox.noBlinksSub")} />;
  return (
    <div className="space-y-3" data-testid="blinks-list">
      {items.map(it => {
        let remaining = it.expires_in_seconds || 0;
        if (it.expires_at) remaining = Math.max(0, Math.floor((new Date(it.expires_at).getTime() - now) / 1000));
        const isUrgent = remaining < 60;
        return (
          <div
            key={it.interest_id}
            data-testid={`blink-${it.interest_id}`}
            onClick={() => { if (!it.read) onOpen(it); }}
            className={`rounded-2xl border ${it.read ? "border-white/10" : "border-[#E60000]/40"} bg-black/60 p-4 flex items-center gap-3 cursor-pointer`}
          >
            <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center relative">
              <Eye size={18} className="text-[#E60000]" />
              {!it.read && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#E60000]" data-testid={`blink-unread-dot-${it.interest_id}`} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{t("inbox.someoneBlinked")}</div>
              <div className="text-xs text-zinc-500 font-mono-accent">{it.from_user.real_name || it.from_user.display_name || t("inbox.someone")}</div>
              <div className={`mt-1 text-[10px] font-mono-accent tracking-widest flex items-center gap-1 ${isUrgent ? "text-[#FF1A1A]" : "text-zinc-500"}`}>
                <Timer size={10} /> {formatRemaining(remaining).toUpperCase()}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onBlinkBack(it); }}
              disabled={remaining <= 0}
              data-testid={`blink-back-${it.interest_id}`}
              className="px-3 py-2 rounded-full bg-gradient-to-r from-[#E60000] to-[#660000] text-white font-bold text-[11px] tracking-wider disabled:opacity-30 active:scale-[0.95] transition"
            >{t("inbox.blinkBack")}</button>
            <button
              onClick={(e) => { e.stopPropagation(); onDismiss(it); }}
              data-testid={`blink-dismiss-${it.interest_id}`}
              aria-label="Dismiss blink"
              className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center transition"
            ><X size={14} className="text-zinc-400" /></button>
          </div>
        );
      })}
    </div>
  );
}

function ChatsList({ items, onOpen, t }) {
  if (!items.length) return <Empty label={t("inbox.noChats")} sub={t("inbox.noChatsSub")} />;
  return (
    <div className="space-y-3" data-testid="chats-list">
      {items.map(c => (
        <button
          key={c.match_id}
          onClick={() => onOpen(c.match_id)}
          data-testid={`chat-${c.match_id}`}
          className={`w-full text-left rounded-2xl border ${c.unread ? "border-[#E60000]/40" : "border-white/10"} bg-black/60 p-4 flex items-center gap-4 hover:border-[#E60000]/40 transition`}
        >
          <div className="w-12 h-12 rounded-full bg-[#1A1A1A] border border-white/10 flex items-center justify-center relative">
            <MessageCircle size={16} className="text-[#E60000]" />
            {c.unread && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#E60000]" data-testid={`chat-unread-dot-${c.match_id}`} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">
              {c.other_user.real_name || c.other_user.display_name || t("inbox.someone")}
              {c.both_kept && <span className="ml-2 text-[9px] font-mono-accent text-[#E60000]">{t("inbox.kept")}</span>}
            </div>
            <div className="text-sm text-zinc-400 truncate mt-0.5">
              {c.last_message ? (c.last_sender_is_me ? t("inbox.you") : "") + c.last_message : t("inbox.sayHi")}
            </div>
            <div className="text-[10px] text-zinc-600 mt-0.5 font-mono-accent">{timeAgo(c.last_message_at).toUpperCase()}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

function CommentsList({ items, onOpen, t }) {
  if (!items.length) return <Empty label={t("inbox.noComments")} sub={t("inbox.noCommentsSub")} />;
  return (
    <div className="space-y-3" data-testid="comments-list">
      {items.map(c => (
        <button
          key={c.id}
          onClick={() => onOpen(c)}
          data-testid={`comment-item-${c.id}`}
          className={`w-full text-left rounded-2xl border ${c.read ? "border-white/10" : "border-[#E60000]/40"} bg-black/60 p-4 hover:border-[#E60000]/40 transition relative`}
        >
          {!c.read && <span className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-[#E60000]" data-testid={`comment-unread-dot-${c.id}`} />}
          <div className="flex justify-between items-start gap-2">
            <div className="text-xs text-zinc-500 font-mono-accent">{c.from.real_name || c.from.display_name || t("inbox.anonymous")}</div>
            <div className="text-[10px] text-zinc-600 font-mono-accent">{timeAgo(c.created_at).toUpperCase()}</div>
          </div>
          <div className="mt-1.5 text-white text-sm leading-snug">{c.text}</div>
          {Object.keys(c.reactions || {}).length > 0 && (
            <div className="mt-2 flex gap-1.5">
              {Object.entries(c.reactions).map(([emoji, uids]) => (
                <span key={emoji} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">
                  {emoji} {uids.length}
                </span>
              ))}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

function Empty({ label, sub }) {
  return (
    <div className="rounded-3xl border border-white/5 bg-black/40 p-8 text-center">
      <p className="text-zinc-300 text-sm">{label}</p>
      <p className="text-zinc-500 text-xs mt-2 leading-relaxed">{sub}</p>
    </div>
  );
}
